import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ListingCard from "@/components/ListingCard";

const resolvePhoto = (p) => p?.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`;

export default function TravelerDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("upcoming");
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    api.get("/bookings/me").then(({ data }) => setBookings(data)).catch(() => {});
    api.get("/favorites").then(({ data }) => setFavorites(data)).catch(() => {});
  }, []);

  const now = new Date().toISOString().slice(0, 10);
  const upcoming = bookings.filter((b) => b.check_out >= now);
  const past = bookings.filter((b) => b.check_out < now);
  const shown = tab === "upcoming" ? upcoming : tab === "past" ? past : [];

  return (
    <div className="pt-24 sm:pt-28 min-h-screen flex">
      <aside className="hidden md:block w-64 bg-[#0A0A0A] text-white min-h-[calc(100vh-6rem)] shrink-0 sticky top-20 self-start">
        <div className="p-6 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">Espace</div>
          <div className="font-display text-xl font-bold mt-1">Voyageur</div>
          <div className="text-xs text-white/60 mt-3 truncate">{user?.email}</div>
        </div>
        <nav className="p-4 flex flex-col gap-1">
          {[
            { k: "upcoming", l: "À venir" },
            { k: "past", l: "Historique" },
            { k: "favorites", l: "Favoris" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`traveler-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`text-left px-4 py-3 text-sm font-semibold transition-colors ${tab === t.k ? "bg-white text-[#0A0A0A]" : "hover:bg-white/10"}`}
            >
              {t.l}
            </button>
          ))}
        </nav>
      </aside>

      <main className="flex-1 px-4 sm:px-6 lg:px-10 py-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-neutral-500 font-bold mb-2">
          {tab === "favorites" ? "Mes favoris" : tab === "past" ? "Historique" : "Mes prochains séjours"}
        </div>
        <h1 className="font-display text-4xl font-bold tracking-tight mb-8">
          {tab === "favorites" ? `${favorites.length} lieu${favorites.length > 1 ? "x" : ""} sauvegardé${favorites.length > 1 ? "s" : ""}` : `${shown.length} séjour${shown.length > 1 ? "s" : ""}`}
        </h1>

        {tab !== "favorites" && (
          <div className="space-y-3">
            {shown.map((b) => (
              <Link
                key={b.id}
                to={`/listings/${b.listing_id}`}
                className="grid grid-cols-1 md:grid-cols-5 gap-3 p-4 border border-black/10 bg-white hover:border-[#0A0A0A] transition-colors"
              >
                <div className="md:col-span-2 font-semibold">{b.listing_title}</div>
                <div className="text-sm">{b.check_in} → {b.check_out}</div>
                <div className="text-sm">{b.guests} voyageur · {b.nights} nuits</div>
                <div className="text-right">
                  <div className="font-bold">{b.amount}€</div>
                  <div className={`text-[10px] uppercase tracking-[0.2em] font-bold mt-1 ${b.payment_status === "paid" ? "text-[#166534]" : "text-neutral-500"}`}>
                    {b.payment_status === "paid" ? "Confirmée" : b.payment_status}
                  </div>
                </div>
              </Link>
            ))}
            {shown.length === 0 && (
              <p className="text-neutral-500 italic">
                {tab === "upcoming" ? "Aucun séjour à venir. " : "Aucun séjour passé."}
                {tab === "upcoming" && <Link to="/search" className="underline font-semibold">Trouver un logement</Link>}
              </p>
            )}
          </div>
        )}

        {tab === "favorites" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {favorites.map((l) => <ListingCard key={l.id} listing={l} isFavorite />)}
            {favorites.length === 0 && (
              <p className="text-neutral-500 italic">Aucun favori. <Link to="/search" className="underline font-semibold">Explorer</Link></p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
